/**
 * @zendir/ui - Toggle/Switch Component
 *
 * Toggle switch following Astro UX Design System with Zendir purple accents.
 *
 * Astro UX Compliance:
 * - Binary on/off control
 * - Clear visual states
 * - Accessible keyboard support
 *
 * Zendir Enhancements:
 * - Purple accent when active
 * - Subtle glow effect
 * - Smooth spring animation
 *
 * @example
 * ```tsx
 * <Toggle checked={enabled} onChange={setEnabled} label="Enable telemetry" />
 * ```
 */

import React, { memo, forwardRef, useId, useState } from "react";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";

export interface ToggleProps {
  /** Checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Label text */
  label?: string;
  /** Label position */
  labelPosition?: "left" | "right";
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: "small" | "medium";
  /** Custom className */
  className?: string;
  /** Name attribute */
  name?: string;
}

export const Toggle = memo(
  forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
    {
      checked = false,
      onChange,
      label,
      labelPosition = "right",
      disabled = false,
      size = "medium",
      className = "",
      name,
    },
    ref,
  ): React.ReactElement {
    const { tokens, theme } = useTheme();
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const id = useId();

    const sizeConfig = {
      small: { width: 34, height: 18, knobSize: 14 },
      medium: { width: 40, height: 22, knobSize: 16 },
    };
    const config = sizeConfig[size];
    // Touch target: 44px minimum via padding around the visual toggle
    const touchPadV = Math.max(0, (44 - config.height) / 2);
    const touchPadH = Math.max(0, (44 - config.width) / 2);

    const handleClick = () => {
      if (!disabled) {
        onChange?.(!checked);
      }
    };

    const isTransparentTheme =
      theme === "transparent" ||
      theme === "transparent-bold" ||
      theme === "transparent-minimal";
    const transparentDefault = tokens.colors.interactive.transparentDefault;
    const transparentHover = tokens.colors.interactive.transparentHover;
    const bgColor = checked
      ? tokens.colors.accent.primary
      : isTransparentTheme && transparentDefault && transparentHover
        ? hovered && !disabled
          ? transparentHover
          : transparentDefault
        : hovered && !disabled
          ? `${tokens.colors.border.muted}`
          : tokens.colors.border.muted;

    const toggle = (
      <div
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        aria-label={!label ? (name ?? "Toggle") : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) =>
          (e.key === " " || e.key === "Enter") &&
          (e.preventDefault(), handleClick())
        }
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          padding: `${touchPadV}px ${touchPadH}px`,
          cursor: disabled ? "not-allowed" : "pointer",
          flexShrink: 0,
          outline: "none",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: config.width,
            height: config.height,
            backgroundColor: bgColor,
            ...(isTransparentTheme &&
              !checked && {
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }),
            borderRadius: config.height / 2,
            opacity: disabled ? 0.5 : 1,
            position: "relative",
            transition: `${tokens.animation.normal}, opacity ${tokens.animation.duration.normal}ms ${tokens.animation.easing.default}`,
            flexShrink: 0,
            boxShadow: checked
              ? `0 0 10px ${tokens.colors.accent.primary}25, inset 0 1px 1px rgba(255,255,255,0.08)`
              : focused
                ? `0 0 0 2px ${tokens.colors.accent.primary}25`
                : "inset 0 1px 2px rgba(0,0,0,0.15)",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: (config.height - config.knobSize) / 2,
              left: checked ? config.width - config.knobSize - 3 : 3,
              width: config.knobSize,
              height: config.knobSize,
              backgroundColor: "#ffffff",
              borderRadius: "50%",
              boxShadow: checked
                ? "0 1px 4px rgba(0,0,0,0.25)"
                : "0 1px 3px rgba(0,0,0,0.2)",
              transition: tokens.animation.spring,
              transform: hovered && !disabled ? "scale(1.05)" : "scale(1)",
            }}
          />
        </div>
        <input
          ref={ref}
          type="checkbox"
          id={id}
          name={name}
          checked={checked}
          disabled={disabled}
          onChange={() => {}}
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: "absolute",
            opacity: 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
          }}
        />
      </div>
    );

    if (!label) {
      return (
        <div className={classNames("zendir-toggle", className)}>{toggle}</div>
      );
    }

    return (
      <label
        htmlFor={id}
        className={classNames("zendir-toggle", className)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          cursor: disabled ? "not-allowed" : "pointer",
          flexDirection: labelPosition === "left" ? "row-reverse" : "row",
        }}
      >
        {toggle}
        <span
          style={{
            fontSize: tokens.typography.fontSize.sm,
            color: disabled
              ? tokens.colors.text.muted
              : tokens.colors.text.primary,
          }}
        >
          {label}
        </span>
      </label>
    );
  }),
);

export default Toggle;
